import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Trash2, Loader2, Eye, EyeOff, Copy, RefreshCw, Mail, Check, Briefcase } from "lucide-react";
import { getQuote, updateQuoteStatus, deleteQuote, rotateShareToken, getEmailStatus, formatUSD } from "@/lib/api";
import SendEmailDialog from "@/components/SendEmailDialog";
import RequestRfqDialog from "@/components/RequestRfqDialog";

const StatRow = ({ label, value, bold, accent, sub }) => (
  <div className="flex items-baseline justify-between py-3 border-b border-[#e5e5e5] last:border-b-0">
    <div>
      <div className={`${bold ? "text-sm font-bold" : "text-xs"} uppercase tracking-[0.16em]`}>{label}</div>
      {sub && <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mt-0.5">{sub}</div>}
    </div>
    <div className={`font-mono ${bold ? "text-base font-bold" : "text-sm"} ${accent ? "text-[#FF3333]" : ""}`}>
      {value}
    </div>
  </div>
);

const Pair = ({ label, value }) => (
  <div className="border border-[#e5e5e5] p-4">
    <div className="text-label mb-2">{label}</div>
    <div className="font-mono text-sm">{value || <span className="text-[#525252]">—</span>}</div>
  </div>
);

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientView, setClientView] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showRfqDialog, setShowRfqDialog] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    getEmailStatus().then(setEmailStatus).catch(() => setEmailStatus({ configured: false }));
  }, []);

  useEffect(() => {
    getQuote(id)
      .then(setQ)
      .catch(() => {
        toast.error("Quote not found");
        navigate("/quotes");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const setStatus = async (status) => {
    try {
      const updated = await updateQuoteStatus(id, status);
      setQ(updated);
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    await deleteQuote(id);
    toast.success("Quote deleted");
    navigate("/quotes");
  };

  const shareUrl = q?.share_token
    ? `${window.location.origin}/q/${q.share_token}`
    : "";

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Client link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const regenerateToken = async () => {
    if (!window.confirm("Regenerate the share link? The old link will stop working.")) return;
    try {
      const res = await rotateShareToken(id);
      setQ({ ...q, share_token: res.share_token });
      toast.success("New share link generated");
    } catch {
      toast.error("Failed to regenerate link");
    }
  };

  if (loading || !q) {
    return (
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-24 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4" strokeWidth={1.5} />
        <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Loading…</div>
      </div>
    );
  }

  const b = q.breakdown;
  const s = q.spec;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="quote-detail-page">
      <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-4">
        <Link to="/quotes" className="ghost-link inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold" data-testid="back-to-quotes">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Quotes
        </Link>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setClientView((v) => !v)}
            data-testid="toggle-view-btn"
            className={`px-3 h-9 text-[11px] uppercase tracking-[0.18em] font-bold border border-[#050505] transition-colors inline-flex items-center gap-2 ${
              clientView ? "bg-[#FF3333] text-white border-[#FF3333]" : "bg-white text-[#050505] hover:bg-[#f5f5f5]"
            }`}
          >
            {clientView ? <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> : <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {clientView ? "Client view" : "Internal view"}
          </button>
          {["draft", "sent", "accepted", "declined"].map((st) => (
            <button
              key={st}
              onClick={() => setStatus(st)}
              data-testid={`set-status-${st}`}
              className={`px-3 h-9 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                q.status === st
                  ? "bg-[#050505] text-white border-[#050505]"
                  : "bg-white text-[#050505] border-[#050505] hover:bg-[#f5f5f5]"
              }`}
            >
              {st}
            </button>
          ))}
          <button
            onClick={() => setShowSendDialog(true)}
            className="btn-primary"
            data-testid="open-send-dialog-btn"
            title="Email quote to client"
          >
            <Mail className="w-4 h-4" strokeWidth={1.5} />
            Send to Client
          </button>
          <button
            onClick={() => setShowRfqDialog(true)}
            className="btn-secondary"
            data-testid="open-rfq-dialog-btn"
            title="Send the book specs to a printer to receive their own quote"
          >
            <Briefcase className="w-4 h-4" strokeWidth={1.5} />
            Request Printer Quote
          </button>
          <button onClick={() => window.print()} className="btn-secondary" data-testid="print-pdf-btn">
            <Printer className="w-4 h-4" strokeWidth={1.5} /> Export PDF
          </button>
          <button onClick={handleDelete} className="btn-secondary" data-testid="delete-quote-btn" title="Delete">
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Last sent indicator */}
      {q.last_email_at && (
        <div className="no-print mb-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#1a7f37] border border-[#1a7f37] px-3 py-1.5" data-testid="last-sent-indicator">
          <Check className="w-3.5 h-3.5" strokeWidth={2} />
          Last sent to {q.last_email_to} on{" "}
          {new Date(q.last_email_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      )}

      {/* Share link panel — internal only */}
      {!clientView && q.share_token && (
        <div className="no-print mb-6 border border-[#050505] bg-[#f5f5f5] p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="share-link-panel">
          <div className="flex-1 min-w-0">
            <div className="text-label mb-1.5">Client share link</div>
            <div className="font-mono text-xs sm:text-sm text-[#050505] truncate" data-testid="share-link-url">
              {shareUrl}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mt-1.5">
              Read-only · No printer cost shown · Client can accept/decline
            </div>
          </div>
          <div className="flex gap-2 self-stretch sm:self-auto">
            <button onClick={copyShareLink} className="btn-primary" data-testid="copy-share-link-btn">
              <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy Link
            </button>
            <button onClick={regenerateToken} className="btn-secondary" data-testid="rotate-share-link-btn" title="Generate new link">
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {/* Invoice-style sheet */}
      <article className="border border-[#050505]">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 border-b border-[#050505]">
          <div className="md:col-span-8 p-6 lg:p-10 md:border-r border-[#050505]">
            <div className="text-label mb-3">Quote</div>
            <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none" data-testid="quote-number">
              {q.quote_number}
            </div>
            <div className="mt-4 text-sm text-[#525252]">
              Issued{" "}
              <span className="font-mono">
                {new Date(q.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
              </span>
              {" · "}
              <span className={`status-pill status-${q.status} align-middle`}>{q.status}</span>
            </div>
          </div>
          <div className="md:col-span-4 p-6 lg:p-10 bg-[#F5F5F5]">
            <div className="text-label mb-3">From</div>
            <div className="font-display font-bold text-lg">2RA-press</div>
            <div className="text-xs text-[#525252] font-mono mt-1">
              Book Quote Studio
              <br />
              quote@2ra-press.studio
              <br />
              All prices in USD
            </div>
          </div>
        </div>

        {/* Client + project */}
        <div className="grid grid-cols-1 md:grid-cols-2 border-b border-[#050505]">
          <div className="p-6 lg:p-10 md:border-r border-[#050505]">
            <div className="text-label mb-3">Project</div>
            <div className="font-display text-2xl font-bold" data-testid="project-title">
              {q.project_title || "Untitled project"}
            </div>
            {q.notes && (
              <div className="mt-4 text-sm text-[#525252] leading-relaxed border-l-2 border-[#050505] pl-4" data-testid="quote-notes">
                {q.notes}
              </div>
            )}
          </div>
          <div className="p-6 lg:p-10">
            <div className="text-label mb-3">Client</div>
            <div className="font-display text-xl font-bold" data-testid="client-name">{q.client_name || "—"}</div>
            <div className="text-sm font-mono text-[#525252] mt-1" data-testid="client-email">{q.client_email || "—"}</div>
          </div>
        </div>

        {/* Specs */}
        <div className="p-6 lg:p-10 border-b border-[#050505]">
          <div className="text-label mb-6">Specifications</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 -ml-px -mt-px">
            <Pair label="Trim size" value={q.labels.trim_size} />
            <Pair label="Page count" value={`${s.page_count} pp`} />
            <Pair label="Interior color" value={q.labels.color_mode} />
            <Pair label="Paper" value={q.labels.paper_type} />
            <Pair label="Cover" value={q.labels.cover_type} />
            <Pair label="Binding" value={q.labels.binding_type} />
            <Pair label="Finishing" value={q.labels.finishing_type} />
            <Pair label="Quantity" value={`${s.quantity} copies`} />
          </div>
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 p-6 lg:p-10 lg:border-r border-[#050505]" data-testid="detail-breakdown">
            {clientView ? (
              <>
                <div className="text-label mb-4">Your Quote</div>
                <StatRow label="Interior printing" sub={`${s.page_count} pp · ${q.labels.color_mode}`} value={`${s.quantity} copies`} />
                <StatRow label="Cover & binding" sub={`${q.labels.cover_type} · ${q.labels.binding_type}`} value={q.labels.finishing_type} />
                {b.tax_rate > 0 && <StatRow label={`Tax (${b.tax_rate}%)`} value={formatUSD(b.tax_amount)} />}
                <div className="mt-4 pt-4 border-t border-[#050505]">
                  <StatRow label="Total" value={formatUSD(b.total)} bold />
                </div>
                <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[#525252]">
                  Pricing valid for 30 days from issue date. All prices in {b.currency}.
                </div>
              </>
            ) : (
              <>
                <div className="text-label mb-4">Printer Cost</div>
                <StatRow label="Interior printing" sub={`${s.page_count} pp × ${s.quantity} · ${q.labels.color_mode}`} value={formatUSD(b.interior_cost)} />
                <StatRow label="Cover" sub={q.labels.cover_type} value={formatUSD(b.cover_cost)} />
                <StatRow label="Binding" sub={q.labels.binding_type} value={formatUSD(b.binding_cost)} />
                <StatRow label="Finishing" sub={q.labels.finishing_type} value={formatUSD(b.finishing_cost)} />
                <StatRow label="Setup" sub="one-time" value={formatUSD(b.setup_fee)} />
                {b.proof_fee > 0 && <StatRow label="Proof" sub="one-time" value={formatUSD(b.proof_fee)} />}
                {b.courier_fee > 0 && (
                  <StatRow
                    label="Shipping"
                    sub={b.estimated_weight_kg ? `${b.estimated_weight_kg.toFixed(2)} kg total` : "weight-based"}
                    value={formatUSD(b.courier_fee)}
                  />
                )}
                {b.discount_pct > 0 && (
                  <StatRow label={`Volume discount (${b.discount_pct}%)`} value={`-${formatUSD(b.discount_amount)}`} accent />
                )}
                <div className="mt-4 pt-4 border-t border-[#050505]">
                  <StatRow label="Printer total" sub={`${formatUSD(b.printer_unit_cost)} / book`} value={formatUSD(b.printer_subtotal)} bold />
                </div>

                {b.markup_pct > 0 && (
                  <div className="mt-6">
                    <div className="text-label mb-3">Markup &amp; Client</div>
                    <StatRow label={`Markup (${b.markup_pct}%)`} sub="your profit" value={`+${formatUSD(b.markup_amount)}`} accent />
                    {b.tax_rate > 0 && <StatRow label={`Tax (${b.tax_rate}%)`} value={formatUSD(b.tax_amount)} />}
                    <div className="mt-2 pt-2 border-t border-[#050505]">
                      <StatRow label="Your profit" sub={`${b.profit_pct}% of revenue`} value={formatUSD(b.profit_amount)} bold accent />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="lg:col-span-5 p-6 lg:p-10 bg-[#F5F5F5] flex flex-col justify-between">
            <div>
              <div className="text-label mb-3">{clientView ? "Total" : "Total Due (Client)"}</div>
              <div className="font-mono font-black text-5xl lg:text-6xl tracking-tighter leading-none text-[#FF3333]" data-testid="detail-total">
                {formatUSD(b.total)}
              </div>
              <div className="mt-4 font-mono text-sm text-[#525252]">
                <span data-testid="detail-unit-price">{formatUSD(b.unit_price)}</span> per book ·{" "}
                <span data-testid="detail-qty">{b.quantity}</span> copies
              </div>
              {!clientView && b.printer_subtotal > 0 && (
                <div className="mt-6 pt-6 border-t border-[#e5e5e5]">
                  <div className="text-label mb-2">Printer Cost</div>
                  <div className="font-mono text-lg" data-testid="detail-printer-cost">
                    {formatUSD(b.printer_subtotal)}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#525252] mt-1">
                    {formatUSD(b.printer_unit_cost)} / book
                  </div>
                </div>
              )}
            </div>
            <div className="mt-8 pt-6 border-t border-[#050505] text-[11px] uppercase tracking-[0.2em] text-[#525252]">
              Quote valid for 30 days · USD
            </div>
          </div>
        </div>
      </article>

      {/* RFQ History — who you've requested quotes from */}
      {Array.isArray(q.rfq_history) && q.rfq_history.length > 0 && (
        <section className="no-print mt-8 border border-[#050505]" data-testid="rfq-history">
          <div className="border-b border-[#050505] px-6 py-4 bg-[#050505] text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Briefcase className="w-4 h-4" strokeWidth={1.5} />
              <h3 className="font-display font-bold text-lg uppercase tracking-tight">
                Printer RFQ History
              </h3>
            </div>
            <div className="font-mono text-[11px] tracking-[0.18em] text-[#aaa]">
              {q.rfq_history.length} sent
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#050505]">
                {["Printer", "Email", "Deadline", "Lang", "Sent at"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.rfq_history.slice().reverse().map((r) => (
                <tr key={r.id || r.email_id} className="border-b border-[#e5e5e5] last:border-b-0">
                  <td className="px-4 py-3">{r.recipient_name || <span className="text-[#525252]">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.recipient_email}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.deadline || <span className="text-[#525252]">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs uppercase">{r.language}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {new Date(r.sent_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {showSendDialog && (
        <SendEmailDialog
          quote={q}
          emailStatus={emailStatus}
          onClose={() => setShowSendDialog(false)}
          onSent={(res) => {
            setQ((prev) => prev && {
              ...prev,
              last_email_at: res.sent_at,
              last_email_to: res.to,
              last_email_id: res.email_id,
              status: prev.status === "draft" ? "sent" : prev.status,
            });
          }}
        />
      )}
      {showRfqDialog && (
        <RequestRfqDialog
          quote={q}
          emailStatus={emailStatus}
          onClose={() => setShowRfqDialog(false)}
          onSent={(res) => {
            setQ((prev) => prev && {
              ...prev,
              rfq_history: [...(prev.rfq_history || []), res.entry],
            });
          }}
        />
      )}
    </div>
  );
}
